import { useState, useRef, useEffect } from 'react';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useManagerPinVerification } from '@/hooks/useReceiptPrints';

interface ManagerPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: (managerId: string, managerName: string) => void;
  title?: string;
  description?: string;
}

export function ManagerPinDialog({
  open,
  onOpenChange,
  onVerified,
  title = 'Manager Approval Required',
  description = 'Enter manager PIN to continue',
}: ManagerPinDialogProps) {
  const PIN_LENGTH = 6;
  const [pin, setPin] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { verifyPin, isVerifying } = useManagerPinVerification();

  // Focus first input when dialog opens
  useEffect(() => {
    if (open) {
      setPin(Array(PIN_LENGTH).fill(''));
      setError(null);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError(null);

    // Auto-focus next input
    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === PIN_LENGTH - 1) {
      const fullPin = newPin.join('');
      handleVerify(fullPin);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (fullPin: string) => {
    try {
      const result = await verifyPin.mutateAsync({ pin: fullPin });
      onVerified(result.managerId, result.managerName);
      onOpenChange(false);
    } catch (err) {
      setError('Invalid PIN. Please try again.');
      setPin(Array(PIN_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
  };

  const handleSubmit = () => {
    const fullPin = pin.join('');
    if (fullPin.length === PIN_LENGTH) {
      handleVerify(fullPin);
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="xs">
        <ResponsiveModalHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
            <Lock className="h-6 w-6 text-amber-600" />
          </div>
          <ResponsiveModalTitle className="text-center">{title}</ResponsiveModalTitle>
          <ResponsiveModalDescription className="text-center">
            {description}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody>
          {/* PIN Input - 6 digits */}
          <div className="flex justify-center gap-2 mb-4">
            {pin.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={cn(
                  'w-11 h-12 text-center text-xl font-bold p-0',
                  error && 'border-red-500 focus-visible:ring-red-500'
                )}
                disabled={isVerifying}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-500 text-center mb-4">{error}</p>
          )}

          {/* Verification status */}
          {isVerifying && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Verifying...</span>
            </div>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="blaze"
            className="flex-1 gap-2"
            onClick={handleSubmit}
            disabled={pin.join('').length !== PIN_LENGTH || isVerifying}
          >
            {isVerifying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            Verify
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
